import "./index.less";
import * as React from "react";
import {
    AppScope,
    GlobalStores,
    IAppContext,
    ItemType,
    ModalStore,
    ModalTypes,
    ReactAppSettings,
    ResponsivenessHelper,
    Role,
    User,
    UserInfoStore,
    UserStore,
} from "@atman/business";
import { AtListHeader } from "../AtListHeader";
import {
    BaseColumn,
    BaseItemList,
    EmailColumn,
    UserButtonsColumn,
    UserDisplayNameColumn,
    UserRolesColumn,
    UserStatusColumn,
} from "../BaseItemList";
import { BaseForm } from "../BaseForm";
import { CustomDeleteAlert } from "../CustomDeleteAlert";
import { IAtButtonProps } from "@atman/design-system";
import { PersonQuickView } from "../PersonQuickView";
import { action, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import { withAppContext } from "../../contexts";
import autobind from "autobind-decorator";

export type UserManagementView = "active" | "pending";

export interface IUserManagementProps extends IAppContext {
    userStore?: UserStore;
    modalStore?: ModalStore;
    userInfoStore?: UserInfoStore;
    ownerId?: string;
    fromSettingsPage?: boolean;
}

@inject(GlobalStores.userStore, GlobalStores.modalStore, GlobalStores.userInfoStore)
@observer
export class UserManagementImpl extends BaseForm<IUserManagementProps, {}> {
    @observable public activeView: UserManagementView = "active";
    @observable public searchString: string = "";

    @computed
    get users() {
        const { ownerId, userStore } = this.props;

        let users: User[];

        if (ownerId) {
            users = userStore!.entityUsers;
        } else {
            users = userStore!.users;
        }

        let usersToReturn: User[];

        const pendingUsers = users.filter((x) => !x.lastLoginDate && x.mustChangePassword);

        switch (this.activeView) {
            case "pending":
                usersToReturn = pendingUsers;
                break;
            case "active":
            default:
                usersToReturn = users.filter((x) => !pendingUsers.find((u) => u.id === x.id));
                break;
        }

        if (!this.searchString) {
            return usersToReturn;
        }

        return usersToReturn.filter(
            (x) =>
                x.firstName.toLowerCase().includes(this.searchString.toLowerCase()) ||
                x.lastName.toLowerCase().includes(this.searchString.toLowerCase()) ||
                `${x.firstName} ${x.lastName}`.toLowerCase().includes(this.searchString.toLowerCase()) ||
                x.email.toLowerCase().includes(this.searchString.toLowerCase()),
        );
    }

    @action.bound
    onFilterChanged = (view: UserManagementView) => {
        this.activeView = view;
    };

    @autobind
    onUserEditClick(id: string) {
        this.props.modalStore!.openModal(ModalTypes.UserForm, { id });
    }

    @autobind
    onUserClientsDelegationUpdateClick(id: string) {
        const { modalStore } = this.props;

        modalStore!.openModal(ModalTypes.UpdateUserClientsDelegation, { id });
    }

    @autobind
    async onUserSecondaryClick(id: string) {
        const { userStore, ownerId } = this.props;

        let user: User | undefined;

        if (ownerId) {
            user = userStore!.entityUsers.find((x) => x.id === id);
        } else {
            user = userStore!.getUserById(id);
        }

        if (!user) {
            return;
        }

        const deleteAlert = new CustomDeleteAlert(
            ItemType.User,
            `${user.firstName} ${user.lastName}`,
            () => userStore!.deleteUser(id, ownerId),
            <PersonQuickView user={user} />,
        );

        return deleteAlert.fire();
    }

    render() {
        const { scope, ownerId, modalStore, userInfoStore, userStore, fromSettingsPage = false } = this.props;

        const createUserModalChildProps: { clientId?: string; partnerProId?: string } = {
            clientId: undefined,
            partnerProId: undefined,
        };
        let isDelegatedForClient: boolean = false;

        switch (scope) {
            case AppScope.Client:
                createUserModalChildProps.clientId = userInfoStore!.clientId;
                createUserModalChildProps.partnerProId = userInfoStore!.partnerId;
                isDelegatedForClient = true;
                break;
            case AppScope.Partner:
                createUserModalChildProps.partnerProId = userInfoStore!.partnerId;
                isDelegatedForClient =
                    fromSettingsPage ||
                    userInfoStore!.automaticClientDelegationEnabled ||
                    userInfoStore!.hasRole(Role.AdminSupplier) ||
                    (ReactAppSettings.appModel.delegatedClientIds ?? []).includes(ownerId!);
                break;
            case AppScope.Supplier:
                isDelegatedForClient = true;
                break;
        }

        const tableColumns: BaseColumn[] = [new UserDisplayNameColumn()];

        if (ResponsivenessHelper.isSizeOrBigger("xxl")) {
            tableColumns.push(new EmailColumn());
        }

        tableColumns.push(new UserRolesColumn());

        if (this.activeView !== "pending") tableColumns.push(new UserStatusColumn());

        const canManageUserClientsDelegation =
            scope === AppScope.Partner && fromSettingsPage && userInfoStore!.automaticClientDelegationEnabled;

        tableColumns.push(
            new UserButtonsColumn(
                this.onUserEditClick,
                this.onUserSecondaryClick,
                userInfoStore!,
                userStore!,
                isDelegatedForClient,
                canManageUserClientsDelegation ? this.onUserClientsDelegationUpdateClick : null,
                !fromSettingsPage,
            ),
        );

        const buttons: IAtButtonProps[] = [
            {
                onClick: () => modalStore!.openModal(ModalTypes.UserForm, createUserModalChildProps),
                children: "global.addUser".localize(),
            },
            {
                onClick: userStore!.loadUsers,
                color: "secondary",
                icon: ["far", "sync"],
                isLoading: userStore!.isLoading,
            },
        ];

        return (
            <div id="UserManagement">
                {fromSettingsPage && (
                    <AtListHeader
                        searchString={this.searchString}
                        onSearchStringChange={this.onTextFieldChange}
                        buttons={buttons}
                    />
                )}
                <div className="filters">
                    <span
                        className={`filter active-filter ${this.activeView === "active" ? "selected" : ""}`}
                        onClick={() => this.onFilterChanged("active")}
                    >
                        {"global.active".localize()}
                    </span>
                    <span
                        className={`filter pending-filter ${this.activeView === "pending" ? "selected" : ""}`}
                        onClick={() => this.onFilterChanged("pending")}
                    >
                        {"global.pending".localize()}
                    </span>
                </div>
                <div className="list-container">
                    <BaseItemList<User>
                        tableRows={this.users.slice()}
                        tableColumns={tableColumns}
                        modalType={ModalTypes.UserForm}
                        selectRow={false}
                        disableClickActionForRow={(row) => {
                            if (!isDelegatedForClient) {
                                return true;
                            }

                            if (row.id === userInfoStore!.userId) {
                                return true;
                            }

                            return false;
                        }}
                        onClickOverride={(e, row) => {
                            if (row.id === userInfoStore!.userId) {
                                return;
                            }

                            const classNames = e.target?.className ?? e.currentTarget?.className;

                            if (typeof classNames === "object") {
                                return;
                            }

                            if (
                                !!classNames &&
                                (classNames.indexOf("AtButton") >= 0 ||
                                    classNames.indexOf("AtMenuButton") >= 0 ||
                                    classNames.indexOf("dropdown-menu") >= 0 ||
                                    classNames.indexOf("dropdown-item") >= 0)
                            ) {
                                return;
                            }

                            modalStore!.openModal(ModalTypes.UserForm, {
                                id: row.id,
                                externalUsers: !!ownerId,
                            });
                        }}
                    />
                </div>
            </div>
        );
    }
}

export const UserManagement = withAppContext(UserManagementImpl);
